'use client';
import { RadioGroup } from '@headlessui/react';
import clsx from 'clsx';
import { get } from 'lodash';
import { useId } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { textVariants } from '../../text';
import { FormFieldMessage } from '../message';
import { RequiredSpan } from '../required';

import { radioButtonVariants } from './style';

import type { RadioFieldProps } from './radio-button.types';
import type { FieldValues } from 'react-hook-form';

const RadioButtonField = <T extends FieldValues>({
  name,
  options,
  heading,
  defaultValue,
  disabled = false,
  hint,
  showHeading = false,
  variant = 'horizontal',
  required,
  onChange = () => null,
}: RadioFieldProps<T>) => {
  const id = useId();
  const {
    control,
    formState: { errors },
  } = useFormContext<T>();

  const error = get(errors, name);

  const { field } = useController({
    control,
    name,
    disabled,
    defaultValue,
  });
  const messageId = `radio-message-${id}`;

  return (
    <div
      className="flex flex-col max-lg:gap-c lg:gap-b"
      data-testid={name}
    >
      <RadioGroup
        {...field}
        onChange={(data) => {
          field.onChange(data);
          onChange(data);
        }}
        className="flex flex-col max-lg:gap-c lg:gap-b"
      >
        {heading ? (
          <RadioGroup.Label
            className={
              showHeading
                ? twMerge(
                    clsx(textVariants({ variant: 'body2', weight: 'semi' }), {
                      'text-sm text-error': Boolean(error),
                    })
                  )
                : 'sr-only'
            }
            role=""
          >
            {heading} {required ? <RequiredSpan /> : null}
          </RadioGroup.Label>
        ) : null}

        <div className={radioButtonVariants({ variant })}>
          {options.map((option) => (
            <RadioGroup.Option
              key={option.value}
              className="group flex flex-1 outline-none"
              value={option.value}
              {...(option.disabled ? { disabled: option.disabled } : {})}
            >
              {({ checked }) => (
                <RadioGroup.Label
                  as="span"
                  className={twMerge(
                    clsx(
                      [
                        'min-h flex min-h-[3rem] flex-1 cursor-pointer select-none items-center justify-center rounded-rad-a bg-white px-c py-b text-center text-sm font-semibold leading-snug outline-none ring-1 ring-gray-200 lg:min-h-[3.5rem]',
                        'group-active:bg-blue-100 group-active:ring-2 group-active:ring-blue-600',
                        'group-hover:ring-2 group-hover:ring-blue-400',
                        'group-focus-visible:ring-2 group-focus-visible:ring-pink-500',
                      ],
                      {
                        'bg-blue-100 ring-2 ring-blue-400': checked,
                        'opacity-30': disabled || option.disabled,
                        'ring-2 ring-error':
                          Boolean(error) && !(disabled || option.disabled),
                      }
                    )
                  )}
                >
                  {option.name}
                </RadioGroup.Label>
              )}
            </RadioGroup.Option>
          ))}
        </div>
      </RadioGroup>

      <FormFieldMessage
        id={messageId}
        hint={hint}
        error={error?.message as string}
      />
    </div>
  );
};

export default RadioButtonField;
